/**
 * Enhanced Virtual Background Processor (Google Meet–like)
 *
 * Features:
 *  - Blur / image / (optional video) background replacement using MediaPipe Selfie Segmentation
 *  - Adaptive segmentation resolution & performance monitoring
 *  - requestVideoFrameCallback when available (better sync & power efficiency)
 *  - Soft edge feathering with optional downscaled mask processing
 *  - Background media caching
 *  - Lifecycle management: start, stop, destroy
 *  - Dynamic option updates & type switching
 *  - Graceful degradation if model load fails (falls back to original video)
 *
 * Usage:
 *  const processor = await createVirtualBackgroundProcessor({
 *    sourceStream: yourMediaStream,
 *    type: 'blur-strong', // or 'image-1', 'image:https://your.cdn/bg.jpg'
 *    blurAmount: 18
 *  });
 *  await processor.start();
 *  const processed = processor.outputStream; // attach to video element or RTCPeerConnection
 *
 *  // Later:
 *  await processor.setType('image-3');
 *  processor.stop(); // pause rendering
 *  processor.destroy(); // full cleanup
 */

const MEDIAPIPE_VERSION = '0.1'; // pin a version for stability (adjust as needed)
const MEDIAPIPE_BASE = `https://cdn.jsdelivr.net/npm/@mediapipe/selfie_segmentation@${MEDIAPIPE_VERSION}`;
const MEDIAPIPE_PRIMARY = `${MEDIAPIPE_BASE}/selfie_segmentation.js`;

let selfieSegmentationLoading = null;
let selfieSegmentationCtorCache = null;

/**
 * Load (or reuse) the SelfieSegmentation constructor.
 */
function loadSelfieSegmentation() {
  if (selfieSegmentationCtorCache) {
    return Promise.resolve(selfieSegmentationCtorCache);
  }
  if (selfieSegmentationLoading) {
    return selfieSegmentationLoading;
  }

  function inject(src) {
    return new Promise((resolve, reject) => {
      if (window.SelfieSegmentation) return resolve(window.SelfieSegmentation);
      const script = document.createElement('script');
      script.src = src;
      script.async = true;
      script.onload = () => {
        if (window.SelfieSegmentation) resolve(window.SelfieSegmentation);
        else reject(new Error('SelfieSegmentation global not found after load'));
      };
      script.onerror = () => reject(new Error('Failed to load MediaPipe Selfie Segmentation script: ' + src));
      document.head.appendChild(script);
    });
  }

  selfieSegmentationLoading = inject(MEDIAPIPE_PRIMARY)
      .then((ns) => {
        // Accept either function or namespace exporting SelfieSegmentation
        if (typeof ns === 'function') {
          selfieSegmentationCtorCache = ns;
        } else if (ns && typeof ns.SelfieSegmentation === 'function') {
          selfieSegmentationCtorCache = ns.SelfieSegmentation;
        } else if (window.SelfieSegmentation && typeof window.SelfieSegmentation === 'function') {
          selfieSegmentationCtorCache = window.SelfieSegmentation;
        } else if (window.SelfieSegmentation && typeof window.SelfieSegmentation.SelfieSegmentation === 'function') {
          selfieSegmentationCtorCache = window.SelfieSegmentation.SelfieSegmentation;
        } else {
          throw new Error('Could not resolve SelfieSegmentation constructor');
        }
        return selfieSegmentationCtorCache;
      })
      .finally(() => {
        selfieSegmentationLoading = null;
      });

  return selfieSegmentationLoading;
}

/**
 * Parse background type string.
 * Accepts:
 *  - 'none'
 *  - 'blur', 'blur-light', 'blur-strong'
 *  - 'image:<url>' or pre-defined image-X
 *  - 'video:<url>' (optional; ensure CORS trusted)
 */
function parseType(t, fallbackBlur = 12) {
  const s = String(t || 'none').trim();
  if (s === 'none') return { mode: 'none', blur: 0 };
  if (s === 'blur-light') return { mode: 'blur', blur: 6 };
  if (s === 'blur-strong') return { mode: 'blur', blur: 24 };
  if (s === 'blur') return { mode: 'blur', blur: fallbackBlur || 12 };

  if (s.startsWith('image:')) return { mode: 'image', url: s.slice(6) };
  if (s.startsWith('video:')) return { mode: 'video', url: s.slice(6) };

  // Predefined image set (demo backgrounds)
  const map = {
    'image-1': 'https://images.unsplash.com/photo-1500530855697-b586d89ba3ee?q=80&w=1920&auto=format&fit=crop',
    'image-2': 'https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=1920&auto=format&fit=crop',
    'image-3': 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1920&q=80',
    'image-4': 'https://images.unsplash.com/photo-1507842217343-583bb7270b66?auto=format&fit=crop&w=1920&q=80',
    'image-5': 'https://images.unsplash.com/photo-1505691723518-36a5ac3be353?auto=format&fit=crop&w=1920&q=80',
    'image-6': 'https://images.unsplash.com/photo-1503676382389-4809596d5290?auto=format&fit=crop&w=1920&q=80',
    'image-7': 'https://images.unsplash.com/photo-1596495577886-d920f1fb7238?auto=format&fit=crop&w=1920&q=80',
    'image-8': 'https://images.unsplash.com/photo-1509062522246-3755977927d7?auto=format&fit=crop&w=1920&q=80',
    'image-9': 'https://images.unsplash.com/photo-1505691938895-1758d7feb511?auto=format&fit=crop&w=1920&q=80',
    'image-10': 'https://images.unsplash.com/photo-1530305408560-82d13781b33a?auto=format&fit=crop&w=1920&q=80',
    'image-11': 'https://images.unsplash.com/photo-1519389950473-47ba0277781c?auto=format&fit=crop&w=1920&q=80',
    'image-12': 'https://images.unsplash.com/photo-1517433456452-f9633a875f6f?auto=format&fit=crop&w=1920&q=80'
  };
  if (map[s]) return { mode: 'image', url: map[s] };

  return { mode: 'none', blur: 0 };
}

/**
 * Draw media with object-fit: cover semantics.
 */
function drawImageCover(ctx, img, dx, dy, dWidth, dHeight) {
  const iw = img.videoWidth || img.naturalWidth || img.width || 1;
  const ih = img.videoHeight || img.naturalHeight || img.height || 1;
  const r = Math.max(dWidth / iw, dHeight / ih);
  const nw = iw * r;
  const nh = ih * r;
  const nx = dx + (dWidth - nw) / 2;
  const ny = dy + (dHeight - nh) / 2;
  ctx.drawImage(img, nx, ny, nw, nh);
}

/**
 * Simple event emitter (internal).
 */
class MiniEmitter {
  constructor() { this.listeners = {}; }
  on(evt, fn) {
    if (!this.listeners[evt]) this.listeners[evt] = new Set();
    this.listeners[evt].add(fn);
    return () => this.off(evt, fn);
  }
  off(evt, fn) {
    if (this.listeners[evt]) this.listeners[evt].delete(fn);
  }
  emit(evt, payload) {
    if (this.listeners[evt]) {
      for (const fn of [...this.listeners[evt]]) {
        try { fn(payload); } catch (e) { /* eslint-disable no-console */ console.error(e); }
      }
    }
  }
}

/**
 * Main factory function.
 */
export async function createVirtualBackgroundProcessor(userOptions) {
  const {
    sourceStream,
    type = 'blur',
    blurAmount = 12,
    segmentationWidth = 256,
    minSegmentationWidth = 160,
    maxSegmentationWidth = 512,
    adaptiveSegmentation = true,
    segmentationFps = 30,              // target segmentation updates per second
    targetFps,                          // if omitted, try videoTrack setting or fallback to 30
    softEdge = {
      enabled: true,
      blurPx: 6,
      alphaAttenuation: 0.98,
      downscaleFactor: 1 // set to 0.5 or 0.25 for cheaper feathering
    },
    performanceMonitor = {
      enabled: true,
      sampleSize: 30,
      upscaleThresholdMs: 12,   // if avg seg < 12ms, can consider upscaling resolution
      downscaleThresholdMs: 24, // if avg seg > 24ms, downscale resolution
      minIntervalMs: 1000
    },
    modelSelection = 1, // 0/1 per MediaPipe spec; 1 = general landscape
    logLevel = 'warn'   // 'debug' | 'info' | 'warn' | 'error'
  } = userOptions || {};

  if (!sourceStream) throw new Error('sourceStream is required');
  const videoTrack = sourceStream.getVideoTracks?.()[0];
  if (!videoTrack) throw new Error('sourceStream has no video track');

  const emitter = new MiniEmitter();
  const log = (lvl, ...args) => {
    const order = ['debug','info','warn','error'];
    if (order.indexOf(lvl) >= order.indexOf(logLevel)) {
      // eslint-disable-next-line no-console
      console[lvl === 'debug' ? 'log' : lvl]('[VB]', ...args);
    }
  };

  // Source video element
  const srcVideo = document.createElement('video');
  srcVideo.playsInline = true;
  srcVideo.muted = true;
  srcVideo.autoplay = true;
  srcVideo.style.position = 'fixed';
  srcVideo.style.left = '-99999px';
  srcVideo.style.top = '-99999px';
  srcVideo.srcObject = new MediaStream([videoTrack]);
  await srcVideo.play().catch(() => {});

  // Output canvas (composited frame)
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  // Foreground / mask / segmentation input canvases
  const maskCanvas = document.createElement('canvas');
  const maskCtx = maskCanvas.getContext('2d', { willReadFrequently: true });

  const fgCanvas = document.createElement('canvas');
  const fgCtx = fgCanvas.getContext('2d');

  const segInputCanvas = document.createElement('canvas');
  const segInputCtx = segInputCanvas.getContext('2d');

  // Optional downscaled mask workspace (for cheaper feathering)
  let maskWorkCanvas = null;
  let maskWorkCtx = null;
  if (softEdge.enabled && softEdge.downscaleFactor && softEdge.downscaleFactor < 1) {
    maskWorkCanvas = document.createElement('canvas');
    maskWorkCtx = maskWorkCanvas.getContext('2d', { willReadFrequently: true });
  }

  // State
  let currentType = type;
  let currentBlur = blurAmount;
  let running = false;
  let destroyed = false;
  let rafId = 0;
  let rVFCId = 0;
  let selfieSeg = null;
  let segmentation = null;
  let lastSegTime = 0;
  let segBusy = false;
  let currentSegWidth = segmentationWidth;
  let bgMedia = null;
  let lastBgUrl = null;

  // Performance stats
  const segTimes = [];
  let lastPerfAdjust = 0;

  // Determine output fps
  const trackSettings = videoTrack.getSettings?.() || {};
  const outputFps = targetFps || trackSettings.frameRate || 30;

  const outputStream = canvas.captureStream(outputFps);

  videoTrack.addEventListener?.('ended', () => {
    log('info', 'Source track ended');
    emitter.emit('track-ended');
    stop();
  });

  /**
   * Ensure background media is loaded / cached if needed for current type.
   */
  async function ensureBgMedia() {
    const parsed = parseType(currentType, currentBlur);
    if ((parsed.mode === 'image' || parsed.mode === 'video') && parsed.url) {
      if (parsed.url === lastBgUrl && bgMedia) return;
      lastBgUrl = parsed.url;

      if (parsed.mode === 'image') {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.decoding = 'async';
        await new Promise((res) => {
          img.onload = () => res(true);
          img.onerror = () => {
            log('warn', 'Failed to load image background', parsed.url);
            res(false);
          };
          img.src = parsed.url;
        });
        bgMedia = img;
      } else if (parsed.mode === 'video') {
        const vid = document.createElement('video');
        vid.src = parsed.url;
        vid.loop = true;
        vid.muted = true;
        vid.playsInline = true;
        await vid.play().catch(() => {});
        bgMedia = vid;
      }
    } else {
      bgMedia = null;
      lastBgUrl = null;
    }
  }

  /**
   * Initialize segmentation model.
   */
  async function initSegmentation() {
    try {
      const ctor = await loadSelfieSegmentation();
      selfieSeg = new ctor({
        locateFile: (file) => `${MEDIAPIPE_BASE}/${file}`
      });
      if (selfieSeg.setOptions) {
        selfieSeg.setOptions({ modelSelection });
      }
      if (selfieSeg.onResults) {
        selfieSeg.onResults((res) => {
          segmentation = res.segmentationMask || res.segmentation || null;
          emitter.emit('segmentation', { available: !!segmentation });
        });
      }
      if (selfieSeg.initialize) {
        await selfieSeg.initialize();
      }
      emitter.emit('model-loaded');
      log('info', 'Segmentation model initialized');
    } catch (e) {
      selfieSeg = null;
      log('warn', 'Failed to initialize segmentation model:', e);
      emitter.emit('model-error', e);
    }
  }

  /**
   * Optionally adapt segmentation resolution based on avg processing time.
   */
  function adaptiveAdjust() {
    if (!adaptiveSegmentation || !performanceMonitor.enabled) return;
    const now = performance.now();
    if (now - lastPerfAdjust < performanceMonitor.minIntervalMs) return;
    if (segTimes.length < performanceMonitor.sampleSize) return;

    const avg = segTimes.reduce((a, b) => a + b, 0) / segTimes.length;
    segTimes.length = 0;
    let changed = false;

    if (avg > performanceMonitor.downscaleThresholdMs && currentSegWidth > minSegmentationWidth) {
      currentSegWidth = Math.max(minSegmentationWidth, Math.round(currentSegWidth * 0.8));
      changed = true;
      log('info', `Adaptive segmentation: downscale to width ${currentSegWidth} (avg ${avg.toFixed(1)}ms)`);
      emitter.emit('adaptive-resolution', { direction: 'down', width: currentSegWidth, avgSegMs: avg });
    } else if (avg < performanceMonitor.upscaleThresholdMs && currentSegWidth < maxSegmentationWidth) {
      currentSegWidth = Math.min(maxSegmentationWidth, Math.round(currentSegWidth * 1.25));
      changed = true;
      log('info', `Adaptive segmentation: upscale to width ${currentSegWidth} (avg ${avg.toFixed(1)}ms)`);
      emitter.emit('adaptive-resolution', { direction: 'up', width: currentSegWidth, avgSegMs: avg });
    }

    if (changed) {
      lastPerfAdjust = now;
    }
  }

  /**
   * Process & feather mask (optionally in a downscaled space).
   */
  function buildMask() {
    if (!segmentation) return;
    const vw = canvas.width;
    const vh = canvas.height;

    let mCtx = maskCtx;
    let targetW = vw;
    let targetH = vh;

    if (softEdge.enabled && maskWorkCtx && softEdge.downscaleFactor > 0 && softEdge.downscaleFactor < 1) {
      targetW = Math.round(vw * softEdge.downscaleFactor);
      targetH = Math.round(vh * softEdge.downscaleFactor);
      if (maskWorkCanvas.width !== targetW || maskWorkCanvas.height !== targetH) {
        maskWorkCanvas.width = targetW;
        maskWorkCanvas.height = targetH;
      }
      mCtx = maskWorkCtx;
    }

    mCtx.clearRect(0, 0, targetW, targetH);
    mCtx.drawImage(segmentation, 0, 0, targetW, targetH);

    if (softEdge.enabled) {
      // Feather using blur filter
      mCtx.filter = `blur(${softEdge.blurPx}px)`;
      mCtx.drawImage(mCtx.canvas, 0, 0, targetW, targetH);
      mCtx.filter = 'none';

      // Optional alpha attenuation
      if (softEdge.alphaAttenuation && softEdge.alphaAttenuation < 1) {
        try {
          const id = mCtx.getImageData(0, 0, targetW, targetH);
          // only attenuate alpha to soften edges; a full loop is smaller due to downscale
          const data = id.data;
          for (let i = 3; i < data.length; i += 4) {
            data[i] = data[i] * softEdge.alphaAttenuation;
          }
          mCtx.putImageData(id, 0, 0);
        } catch (e) {
          // ignore if not allowed
        }
      }
      if (mCtx !== maskCtx) {
        // Upscale to full maskCanvas
        maskCtx.clearRect(0, 0, vw, vh);
        maskCtx.drawImage(maskWorkCanvas, 0, 0, vw, vh);
      }
    } else if (mCtx !== maskCtx) {
      maskCtx.clearRect(0, 0, vw, vh);
      maskCtx.drawImage(maskWorkCanvas, 0, 0, vw, vh);
    }
  }

  /**
   * Draw composite frame.
   */
  function compositeFrame() {
    const vw = srcVideo.videoWidth || 640;
    const vh = srcVideo.videoHeight || 480;
    if (!vw || !vh) return;

    if (canvas.width !== vw || canvas.height !== vh) {
      canvas.width = vw;
      canvas.height = vh;
      maskCanvas.width = vw;
      maskCanvas.height = vh;
      fgCanvas.width = vw;
      fgCanvas.height = vh;
      emitter.emit('resize', { width: vw, height: vh });
    }

    const parsed = parseType(currentType, currentBlur);

    // Background layer
    try {
      if ((parsed.mode === 'image' || parsed.mode === 'video') && bgMedia) {
        drawImageCover(ctx, bgMedia, 0, 0, vw, vh);
      } else if (parsed.mode === 'blur') {
        ctx.save();
        ctx.filter = `blur(${parsed.blur}px)`;
        ctx.drawImage(srcVideo, 0, 0, vw, vh);
        ctx.restore();
      } else {
        ctx.drawImage(srcVideo, 0, 0, vw, vh);
      }
    } catch (e) {
      ctx.drawImage(srcVideo, 0, 0, vw, vh);
    }

    if (segmentation) {
      buildMask();
      fgCtx.clearRect(0, 0, vw, vh);
      // Draw mask to fg
      fgCtx.globalCompositeOperation = 'source-over';
      fgCtx.drawImage(maskCanvas, 0, 0, vw, vh);
      fgCtx.globalCompositeOperation = 'source-in';
      fgCtx.drawImage(srcVideo, 0, 0, vw, vh);
      fgCtx.globalCompositeOperation = 'source-over';
      ctx.drawImage(fgCanvas, 0, 0, vw, vh);
    }
  }

  /**
   * Attempt segmentation update if time.
   */
  function maybeRunSegmentation() {
    if (!selfieSeg || segBusy) return;
    const now = performance.now();
    const minInterval = 1000 / segmentationFps;
    if (lastSegTime && (now - lastSegTime) < minInterval) return;

    if (!srcVideo.videoWidth || !srcVideo.videoHeight) return;

    const vw = srcVideo.videoWidth;
    const vh = srcVideo.videoHeight;
    const ratio = currentSegWidth / vw;
    const segH = Math.max(1, Math.round(vh * ratio));

    if (segInputCanvas.width !== currentSegWidth || segInputCanvas.height !== segH) {
      segInputCanvas.width = currentSegWidth;
      segInputCanvas.height = segH;
    }

    try {
      segInputCtx.drawImage(srcVideo, 0, 0, currentSegWidth, segH);
      segBusy = true;
      const start = performance.now();
      const maybePromise = selfieSeg.send({ image: segInputCanvas });

      const after = (ok) => {
        const dur = performance.now() - start;
        if (ok) {
          segTimes.push(dur);
          if (segTimes.length > (performanceMonitor.sampleSize || 30)) segTimes.shift();
          adaptiveAdjust();
        }
        segBusy = false;
        lastSegTime = performance.now();
      };

      if (maybePromise && typeof maybePromise.then === 'function') {
        maybePromise.then(() => after(true)).catch((e) => { log('debug', 'Seg send error', e); after(false); });
      } else {
        // Assume synchronous
        after(true);
      }
    } catch (e) {
      segBusy = false;
    }
  }

  /**
   * Main rendering loop (requestVideoFrameCallback preferred).
   */
  function frameLoopRaf() {
    if (!running || destroyed) return;
    compositeFrame();
    maybeRunSegmentation();
    rafId = requestAnimationFrame(frameLoopRaf);
  }

  function frameLoopRVFC(now, meta) {
    if (!running || destroyed) return;
    compositeFrame();
    maybeRunSegmentation();
    if ('requestVideoFrameCallback' in srcVideo) {
      rVFCId = srcVideo.requestVideoFrameCallback(frameLoopRVFC);
    } else {
      rafId = requestAnimationFrame(frameLoopRaf);
    }
  }

  /**
   * Public: start processing.
   */
  async function start() {
    if (destroyed) throw new Error('Processor already destroyed');
    if (running) return;
    running = true;
    await ensureBgMedia();
    if (!selfieSeg) {
      await initSegmentation();
    }
    if ('requestVideoFrameCallback' in srcVideo) {
      rVFCId = srcVideo.requestVideoFrameCallback(frameLoopRVFC);
    } else {
      rafId = requestAnimationFrame(frameLoopRaf);
    }
    emitter.emit('started');
  }

  /**
   * Public: stop (pause) processing but keep resources allocated.
   */
  function stop() {
    if (!running) return;
    running = false;
    if (rafId) cancelAnimationFrame(rafId);
    rafId = 0;
    if (rVFCId && srcVideo.cancelVideoFrameCallback) {
      try { srcVideo.cancelVideoFrameCallback(rVFCId); } catch (_) {}
      rVFCId = 0;
    }
    segBusy = false;
    emitter.emit('stopped');
  }

  /**
   * Public: destroy and free all resources.
   */
  function destroy() {
    if (destroyed) return;
    stop();
    destroyed = true;
    try { srcVideo.pause(); } catch (_) {}
    try { srcVideo.srcObject = null; } catch (_) {}
    if (selfieSeg && selfieSeg.close) {
      try { selfieSeg.close(); } catch (_) {}
    }
    // Stop output track(s)
    try { outputStream.getTracks().forEach(t => t.stop()); } catch (_) {}
    // Null references
    segmentation = null;
    selfieSeg = null;
    bgMedia = null;
    emitter.emit('destroyed');
  }

  /**
   * Public: switch background type.
   */
  async function setType(newType) {
    currentType = newType || 'none';
    await ensureBgMedia();
    emitter.emit('type-changed', { type: currentType });
  }

  /**
   * Public: update runtime options (subset).
   */
  function updateOptions(opts = {}) {
    if (typeof opts.blurAmount === 'number') currentBlur = opts.blurAmount;
    if (typeof opts.segmentationWidth === 'number') currentSegWidth = opts.segmentationWidth;
    if (opts.softEdge && typeof opts.softEdge === 'object') {
      Object.assign(softEdge, opts.softEdge);
    }
    emitter.emit('options-updated', { currentBlur, currentSegWidth, softEdge: { ...softEdge } });
  }

  /**
   * Public: stats snapshot.
   */
  function getStats() {
    return {
      running,
      destroyed,
      currentType,
      blur: currentBlur,
      currentSegWidth,
      outputFps,
      segBusy,
      modelLoaded: !!selfieSeg,
      adaptiveSegmentation,
      softEdge: { ...softEdge }
    };
  }

  /**
   * Public: on/off event subscription
   */
  function on(evt, handler) { return emitter.on(evt, handler); }
  function off(evt, handler) { return emitter.off(evt, handler); }

  // Return public API
  return {
    outputStream,
    start,
    stop,
    destroy,
    setType,
    updateOptions,
    getStats,
    on,
    off
  };
}